import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Store, MapPin, Eye, Save, FileText, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { toast } from 'sonner';
import { API_BASE as API } from '../lib/apiBase';

const eurToCents = (s) => {
  const n = parseFloat(String(s).replace(',', '.'), 10);
  if (Number.isNaN(n) || n < 0) return 0;
  return Math.round(n * 100);
};

const centsToEur = (c) => (c == null || c === '' ? '' : (c / 100).toFixed(2));

function BookingPreviewCard({ profile, artistName, lang, isPublished }) {
  const city = profile?.location?.city || '';
  const country = profile?.location?.country || '';
  const mode = profile?.pricing_mode || 'fixed';
  const cents = profile?.list_price_cents;
  let priceLine = '';
  if (mode === 'inquire') {
    priceLine = lang === 'es' ? (profile?.inquire_cta_es || 'Consultar disponibilidad y presupuesto de contratación') : (profile?.inquire_cta_en || 'Inquire about availability and booking budget');
  } else if (cents != null) {
    const e = (cents / 100).toFixed(0);
    priceLine = mode === 'from' ? (lang === 'es' ? `Desde ${e} €` : `From ${e} €`) : `${e} €`;
  } else {
    priceLine = '—';
  }
  return (
    <div className="relative overflow-hidden rounded-2xl border border-pulse-border bg-pulse-elevated p-5">
      {!isPublished && (
        <div className="absolute top-2 right-2 z-10 rounded-full bg-amber-500/20 text-amber-200 px-2 py-0.5 text-[10px] font-semibold">
          {lang === 'es' ? 'Borrador' : 'Draft'}
        </div>
      )}
      <p className="text-[10px] uppercase tracking-widest text-pulse-muted mb-1">{lang === 'es' ? 'Vista previa pública' : 'Public preview'}</p>
      <h3 className="font-heading font-bold text-lg">{artistName || (lang === 'es' ? 'Tu nombre' : 'Your name')}</h3>
      <p className="text-sm text-pulse-primary mt-1 font-semibold">{priceLine}</p>
      <p className="text-xs text-pulse-muted mt-2 inline-flex items-center gap-1">
        <MapPin size={12} />
        {city || country ? [city, country].filter(Boolean).join(', ') : (lang === 'es' ? 'Base / desplazamiento' : 'Base / travel')}
      </p>
      {(profile?.visibility_note_es || profile?.visibility_note_en) && (
        <p className="text-xs text-pulse-muted mt-3 border-t border-pulse-border pt-3">
          {lang === 'es' ? (profile?.visibility_note_es || profile?.visibility_note_en) : (profile?.visibility_note_en || profile?.visibility_note_es)}
        </p>
      )}
    </div>
  );
}

export default function ArtistBookingPage() {
  const { token, user, refreshUser } = useAuth();
  const { lang } = useLang();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flags, setFlags] = useState({ PAYMENT_RAIL: 'wallet', BOOKING_V2: false });
  const [form, setForm] = useState({
    status: 'draft',
    pricing_mode: 'fixed',
    list_price: '',
    currency: 'EUR',
    loc_city: '',
    loc_country: '',
    note_es: '',
    note_en: '',
  });
  const [docRef, setDocRef] = useState(null);
  const artistName = user?.name || '';

  useEffect(() => {
    if (form.pricing_mode === 'inquire') return;
    if (form.pricing_mode === 'fixed' || form.pricing_mode === 'from') {
      if (form.list_price === '' || eurToCents(form.list_price) < 1) {
        setForm((f) => ({ ...f, pricing_mode: 'inquire', list_price: '' }));
      }
    }
  }, [form.list_price, form.pricing_mode]);

  const load = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await fetch(`${API}/api/booking-profile`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 404) { toast.error(lang === 'es' ? 'Crea un perfil de artista' : 'Create an artist profile first'); }
        setLoading(false);
        return;
      }
      setFlags(data.feature_flags || {});
      setDocRef(data.document);
      const bp = data.booking_profile || {};
      setForm({
        status: bp.status || 'draft',
        pricing_mode: bp.pricing_mode || 'fixed',
        list_price: bp.list_price_cents != null ? centsToEur(bp.list_price_cents) : '',
        currency: bp.currency || 'EUR',
        loc_city: (bp.location && bp.location.city) || '',
        loc_country: (bp.location && bp.location.country) || '',
        note_es: bp.visibility_note_es || '',
        note_en: bp.visibility_note_en || '',
      });
    } catch {
      toast.error('Error de conexion');
    } finally { setLoading(false); }
  }, [token, lang]);

  useEffect(() => { load(); }, [load]);

  if (!token) {
    return (
      <div className="min-h-screen pt-20 px-4 text-center text-pulse-muted">
        <p>{lang === 'es' ? 'Inicia sesion para acceder' : 'Sign in to access'}</p>
        <Link to="/login" className="text-pulse-primary text-sm mt-2 inline-block">{lang === 'es' ? 'Entrar' : 'Login'}</Link>
      </div>
    );
  }
  if (user?.role && user.role !== 'artist' && user.role !== 'admin' && user.role !== 'pluka') {
    return (
      <div className="min-h-screen pt-20 px-4 text-center text-pulse-muted">
        <p>{lang === 'es' ? 'Disponible solo para cuentas artista' : 'For artist accounts only'}</p>
        <Link to="/" className="text-pulse-primary text-sm mt-2 inline-block">Pulse</Link>
      </div>
    );
  }

  if (loading) {
    return <div className="min-h-screen pt-14 flex items-center justify-center"><div className="w-10 h-10 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  const buildPayload = () => {
    const list_price_cents = form.list_price === '' ? null : eurToCents(form.list_price);
    return {
      status: form.status,
      pricing_mode: form.pricing_mode,
      list_price_cents: form.pricing_mode === 'inquire' ? null : list_price_cents,
      currency: form.currency || 'EUR',
      location: { city: form.loc_city, country: form.loc_country },
      visibility_note_es: form.note_es,
      visibility_note_en: form.note_en,
    };
  };

  const previewProfile = (() => {
    const p = buildPayload();
    return {
      status: p.status,
      pricing_mode: p.pricing_mode,
      list_price_cents: p.list_price_cents,
      location: p.location,
      visibility_note_es: p.visibility_note_es,
      visibility_note_en: p.visibility_note_en,
    };
  })();

  const save = async () => {
    if (!token) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/booking-profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(buildPayload()),
      });
      const d = await res.json();
      if (res.ok) {
        setFlags(d.feature_flags || {});
        toast.success(lang === 'es' ? 'Guardado' : 'Saved');
        await refreshUser?.();
      } else {
        toast.error(d.detail || 'Error');
      }
    } catch { toast.error('Error'); }
    setSaving(false);
  };

  return (
    <div data-testid="artist-booking-page" className="min-h-screen pt-14 pb-28">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <Link to="/profile" className="flex items-center gap-2 text-pulse-muted hover:text-white mb-4 transition-colors text-sm">
          <ArrowLeft size={18} /> {lang === 'es' ? 'Volver' : 'Back'}
        </Link>

        <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-pulse-border bg-pulse-surface px-3 py-1 text-xs text-pulse-muted">
          <FileText size={12} className="text-pulse-primary" />
          {lang === 'es' ? 'Sistema de contratacion (no es solo el perfil publico)' : 'Booking system (not just public profile)'}
        </div>
        <h1 className="font-heading font-bold text-2xl flex items-center gap-2">
          <Store size={24} className="text-pulse-primary" />
          {lang === 'es' ? 'Perfil de contratacion' : 'Booking profile'}
        </h1>
        <p className="text-xs text-pulse-muted mt-2 max-w-2xl">
          {lang === 'es'
            ? 'Aqui configuras el punto de partida de toda oferta y contratacion. Los cambios estructurales a pagos o escrow vendran en fases posteriores con flags; tu borrador y publicado son la fuente de verdad hacia el publico.'
            : 'Configure the basis for offers and bookings. Payment rail changes are phased behind flags; draft and published are your public source of truth.'}
        </p>

        {flags && (
          <p className="text-[10px] text-pulse-muted mt-3">
            {lang === 'es' ? 'Riel de pago' : 'Payment rail'}: <code className="text-pulse-primary">{flags.PAYMENT_RAIL || 'wallet'}</code> · V2:{' '}
            <code className="text-pulse-primary">{String(flags.BOOKING_V2)}</code>
          </p>
        )}

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="rounded-2xl border border-pulse-border bg-pulse-surface p-4">
              <h2 className="font-heading text-sm font-semibold mb-3 flex items-center gap-2">
                <Shield size={16} className="text-cyan-400" /> {lang === 'es' ? 'Visibilidad' : 'Visibility'}
              </h2>
              <div className="flex gap-2">
                {['draft', 'published'].map((s) => (
                  <button
                    key={s}
                    data-testid={`artist-booking-status-${s}`}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, status: s }))}
                    className={`flex-1 rounded-xl border px-3 py-2 text-xs font-medium transition ${form.status === s ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-200' : 'border-pulse-border bg-pulse-elevated text-pulse-muted'}`}
                  >
                    {s === 'draft' ? (lang === 'es' ? 'Borrador' : 'Draft') : (lang === 'es' ? 'Publicado' : 'Published')}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-pulse-border bg-pulse-surface p-4">
              <h2 className="font-heading text-sm font-semibold mb-3">{lang === 'es' ? 'Modelo de precio' : 'Price model'}</h2>
              <select
                data-testid="artist-booking-pricing-mode"
                className="w-full rounded-xl border border-pulse-border bg-pulse-elevated px-3 py-2.5 text-sm"
                value={form.pricing_mode}
                onChange={(e) => setForm((f) => ({ ...f, pricing_mode: e.target.value }))}
              >
                <option value="fixed">{lang === 'es' ? 'Precio fijo' : 'Fixed price'}</option>
                <option value="from">{lang === 'es' ? 'Desde (orientativo)' : 'From (guide)'}</option>
                <option value="inquire">{lang === 'es' ? 'Solo consultar' : 'Inquire only'}</option>
              </select>
              {form.pricing_mode !== 'inquire' && (
                <div className="mt-3">
                  <label className="text-[10px] text-pulse-muted uppercase block mb-1">EUR (IVA/impuestos: asesoramiento posterior)</label>
                  <input
                    data-testid="artist-booking-price"
                    className="w-full rounded-xl border border-pulse-border bg-pulse-elevated px-3 py-2.5 text-sm"
                    value={form.list_price}
                    onChange={(e) => setForm((f) => ({ ...f, list_price: e.target.value }))}
                    placeholder="1200"
                    inputMode="decimal"
                  />
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-pulse-border bg-pulse-surface p-4">
              <h2 className="font-heading text-sm font-semibold mb-3 flex items-center gap-2">
                <MapPin size={16} className="text-pulse-primary" />
                {lang === 'es' ? 'Fase 1: ciudad / pais' : 'Phase 1: city / country'}
              </h2>
              <div className="grid grid-cols-1 gap-2">
                <input
                  className="rounded-xl border border-pulse-border bg-pulse-elevated px-3 py-2.5 text-sm"
                  value={form.loc_city}
                  onChange={(e) => setForm((f) => ({ ...f, loc_city: e.target.value }))}
                  placeholder={lang === 'es' ? 'Ciudad' : 'City'}
                />
                <input
                  className="rounded-xl border border-pulse-border bg-pulse-elevated px-3 py-2.5 text-sm"
                  value={form.loc_country}
                  onChange={(e) => setForm((f) => ({ ...f, loc_country: e.target.value }))}
                  placeholder={lang === 'es' ? 'Pais' : 'Country'}
                />
              </div>
            </div>

            <div className="rounded-2xl border border-pulse-border bg-pulse-surface p-4">
              <h2 className="font-heading text-sm font-semibold mb-3">{lang === 'es' ? 'Nota visible' : 'Visible note'}</h2>
              <textarea
                className="w-full rounded-xl border border-pulse-border bg-pulse-elevated px-3 py-2.5 text-sm min-h-[72px]"
                value={form.note_es}
                onChange={(e) => setForm((f) => ({ ...f, note_es: e.target.value }))}
                placeholder="ES: condiciones, exclusiones, rider basico…"
              />
              <textarea
                className="w-full mt-2 rounded-xl border border-pulse-border bg-pulse-elevated px-3 py-2.5 text-sm min-h-[72px]"
                value={form.note_en}
                onChange={(e) => setForm((f) => ({ ...f, note_en: e.target.value }))}
                placeholder="EN: same in English (optional)…"
              />
            </div>

            <button
              data-testid="artist-booking-save"
              type="button"
              onClick={save}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 rounded-2xl bg-pulse-primary text-black py-3 font-semibold text-sm disabled:opacity-50"
            >
              <Save size={16} /> {saving ? '…' : (lang === 'es' ? 'Guardar' : 'Save')}
            </button>
          </div>

          <div>
            <div className="sticky top-20 space-y-3">
              <h2 className="font-heading text-sm font-semibold flex items-center gap-2 text-pulse-muted">
                <Eye size={16} className="text-cyan-400" /> {lang === 'es' ? 'Obligatorio: vista previa' : 'Required: preview'}
              </h2>
              <BookingPreviewCard profile={previewProfile} artistName={artistName} lang={lang} isPublished={form.status === 'published'} />
              {docRef && (
                <p className="text-[10px] text-pulse-muted border border-pulse-border/60 rounded-lg p-2">
                  {lang === 'es' ? 'Estructura base (F0):' : 'Base schema (F0):'}
                  <code className="block text-[9px] mt-1 break-all">schema {docRef.schema_version || 1}</code>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
